import DynamicsLiveControl from './DynamicsLiveControl';
import {isCorrectionConfirmation, isTaskNavigationVoiceCommand, isCorrectionVoiceCommand} from '../../utils/dynamicsVoiceCommands';
import { dynamicsSourceHref } from '../../utils/dynamicsLinks';
import DynamicsIcon from '../common/DynamicsIcon';
import React, { useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Mic, RotateCcw, User, X,
} from 'lucide-react';
import backendApi from '../../services/backendApi';

const SUGGESTIONS = [
  'Quelles fonctionnalités sont disponibles ?',
  'Quels comportements sont définis dans les critères d’acceptation ?',
  'Quels éléments sont encore dans le backlog ?',
];

const welcomeMessage = (projectName, pageContext) => ({
  id: 'welcome',
  role: 'assistant',
  text: pageContext ? `Je suis Dynamix. Je m’appuie d’abord sur « ${pageContext.title} » et ses filtres pour répondre à vos questions. Les réponses indiqueront leurs sources.` : `Je suis Dynamix. Posez une question sur la documentation ou le backlog de ${projectName || 'ce projet'}. Les réponses indiqueront leurs sources.`,
  sources: [],
});

function MessageText({ text, sources = [], onOpen }) {
  return String(text || '').split('\n').filter(Boolean).map((line, index) => {
    const parts = line.split(/(\[[^\]\n]+\]\(S\d+\)|\[S\d+\]|\*\*[^*]+\*\*)/g);
    return <p key={index} className="mb-1.5 last:mb-0">{parts.map((part, partIndex) => {
      const named = part.match(/^\[([^\]]+)\]\((S\d+)\)$/);
      const reference = named?.[2] || (/^\[S\d+\]$/.test(part) ? part.slice(1,-1) : null);
      if (reference) {
        const source = sources.find(item => item.id === reference);
        const href = source && dynamicsSourceHref(source);
        if (!href) return <span key={partIndex} title={source?.title}>{named?.[1] || part}</span>;
        return <a key={partIndex} href={href} title={source.title} className="font-medium text-[#76478a] underline decoration-[#b497c0] underline-offset-2 hover:text-[#523467]" onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault(); onOpen(source);
        }}>{named?.[1] || source.title}</a>;
      }
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={partIndex}>{part.slice(2,-2)}</strong>;
      return <React.Fragment key={partIndex}>{part.replace(/^[-*]\s+/, '• ')}</React.Fragment>;
    })}</p>;
  });
}

export default function DocumentationAssistant({
  open,
  floating = false,
  pageContext,
  onClose,
  project,
  documentationId,
  version,
  isInternal,
  onNavigateSource,
  onApplyFilters,
  onActivityState,
  onUiAction,
  wakeWord,
  autoStartLiveToken,
  autoStartLivePrompt,
}) {
  const [mode, setMode] = useState(pageContext?.mode || 'functional');
  const [messages, setMessages] = useState(() => [welcomeMessage(project?.name, pageContext)]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [liveStatus, setLiveStatus] = useState('idle');
  const [pendingUiAction, setPendingUiAction] = useState(null);
  const suggestions = pageContext ? ['Résume les éléments affichés.', 'Quels points nécessitent mon attention ?', 'Explique le statut des éléments affichés.'] : SUGGESTIONS;
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;

  useEffect(() => {
    const activity = loading || ['connecting','thinking'].includes(liveStatus)
      ? 'thinking'
      : liveStatus === 'speaking' ? 'speaking'
      : liveStatus === 'listening' ? 'listening'
      : 'idle';
    onActivityState?.(activity);
    return () => onActivityState?.('idle');
  }, [loading, liveStatus, onActivityState]);

  const contextKey = context => JSON.stringify([
    project?.odooProjectId || null,
    context?.path || null,
    context?.focusedRecordId || null,
  ]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const resetConversation = () => {
    setMessages([welcomeMessage(project?.name, pageContext)]);
    setQuestion('');
    setPendingUiAction(null);
    inputRef.current?.focus();
  };

  const sendQuestion = async value => {
    const text = String(value || question).trim();
    if (!text || loading || !project?.odooProjectId || pageContext?.ready === false) return;
    if (isCorrectionConfirmation(text) || (pendingUiAction && !pendingUiAction.feedback && !isTaskNavigationVoiceCommand(text) && !isCorrectionVoiceCommand(text))) {
      const requestContextKey = contextKey(pageContext);
      setMessages(current => [...current, {id:`user-${Date.now()}`,role:'user',text,sources:[],contextKey:requestContextKey}]);
      setQuestion('');setLoading(true);
      try {
        if (!onUiAction) throw new Error('La correction n’est pas disponible sur cette page.');
        if (!pendingUiAction) throw new Error('Aucune correction n’est prête à être envoyée. Demandez une correction pour ouvrir le formulaire.');
        if (pageContext?.focusedRecordId !== pendingUiAction.taskId) throw new Error('La User Story a changé. Ouvrez à nouveau sa demande de correction.');
        if (!isCorrectionConfirmation(text)) {
          const action={...pendingUiAction,feedback:text};
          const receipt=await onUiAction(action);
          if (receipt?.prepared !== true) throw new Error('Le formulaire de correction n’a pas pu être préparé.');
          setPendingUiAction(action);
          const response={answer:`Votre feedback est dans le formulaire : « ${text} ». Vérifiez-le puis dites « Confirmer l’envoi ». Rien n’est encore envoyé.`,sources:[],actionStatus:'prepared'};
          setMessages(current=>[...current,{id:`assistant-${Date.now()}`,role:'assistant',text:response.answer,sources:[],contextKey:requestContextKey}]);
          return response;
        }
        const receipt=await onUiAction({...pendingUiAction,type:'submit_correction'});
        if (receipt?.submitted !== true) throw new Error('L’enregistrement de la correction n’a pas été confirmé.');
        const response={actionStatus:'submitted',answer:'La demande de correction et votre feedback ont bien été enregistrés.',sources:[],insufficientContext:false};
        setMessages(current=>[...current,{id:`assistant-${Date.now()}`,role:'assistant',text:response.answer,sources:[],contextKey:requestContextKey}]);
        setPendingUiAction(null);
        return response;
      } catch(error) {
        const message=error?.message || 'La demande de correction n’a pas pu être enregistrée.';
        setMessages(current=>[...current,{id:`error-${Date.now()}`,role:'error',text:message,sources:[],contextKey:requestContextKey}]);
        return {error:message};
      } finally {setLoading(false);inputRef.current?.focus();}
    }
    const requestContextKey = contextKey(pageContext);
    const history = messages
      .filter(message => message.id !== 'welcome' && ['user', 'assistant'].includes(message.role))
      .filter(message => !message.contextKey || message.contextKey === requestContextKey)
      .slice(-10)
      .map(message => ({ role: message.role, content: message.text.slice(0, 2000) }));
    const userMessage = { id: `user-${Date.now()}`, role: 'user', text, sources: [], contextKey:requestContextKey };
    setMessages(current => [...current, userMessage]);
    setQuestion('');
    setLoading(true);
    try {
      const response = await backendApi.askDocumentationAssistant({
        question: text,
        projectId: project.odooProjectId,
        documentationId,
        version,
        mode: isInternal ? mode : 'functional',
        history,
        ...(pageContext ? {pageContext} : {}),
      });
      if (response.filters) onApplyFilters?.(response.filters);
      if (response.uiAction) {
        if (!onUiAction) throw new Error('La correction n’est pas disponible sur cette page.');
        const receipt=await onUiAction(response.uiAction);
        if (receipt?.prepared !== true) throw new Error('Le formulaire de correction n’a pas pu être ouvert.');
        response.actionStatus='prepared';
        setPendingUiAction(response.uiAction);
      }
      setMessages(current => [...current, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: response.answer,
        sources: response.sources || [],
        insufficientContext: response.insufficientContext,
        contextKey: requestContextKey,
      }]);
      if (response.openSource && contextKey(pageContextRef.current) === requestContextKey) openSource(response.openSource);
      return response;
    } catch (error) {
      setMessages(current => [...current, { id: `error-${Date.now()}`, role: 'error', text: error.message, sources: [] }]);
      return {error:error.message};
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const openSource = source => {
    if (!dynamicsSourceHref(source)) return;
    onNavigateSource?.(source);
  };

  if (!open) return null;
  return (
    <aside className={floating ? "dynamics-panel dynamics-conversation-bubble" : "fixed inset-0 z-[70] flex min-w-0 flex-col bg-white lg:sticky lg:top-0 lg:z-20 lg:h-screen lg:w-[420px] lg:shrink-0 lg:border-l lg:border-slate-200"} aria-label="Assistant documentaire">
      <header className="dynamics-conversation-header border-b border-slate-200 bg-[#382443] px-5 py-4 text-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            {!floating && <DynamicsIcon size={36} state={loading ? 'thinking' : 'idle'} />}
            <div className="min-w-0"><h2 className="font-semibold">Dynamix</h2><p className="truncate text-xs text-white/65">{project?.name}</p></div>
          </div>
          <div className="flex gap-1">
            {wakeWord && <button type="button" onClick={wakeWord.toggle} disabled={!wakeWord.supported} aria-pressed={wakeWord.enabled} title={wakeWord.supported ? 'Dire « Dynamix » pour ouvrir et démarrer la conversation' : 'Réveil vocal non disponible dans ce navigateur'} className={`dynamics-wake-toggle ${wakeWord.enabled ? 'is-enabled' : ''}`}><Mic className="h-4 w-4"/><span>Réveil vocal</span></button>}
            <button type="button" onClick={resetConversation} aria-label="Nouvelle conversation" className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white"><RotateCcw className="h-4 w-4" /></button>
            <button type="button" onClick={onClose} aria-label="Fermer l’assistant" className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
        </div>
        {pageContext && <p className="mt-3 text-xs text-white/80">Contexte : {pageContext.title}{pageContext.recordIds ? ` · ${pageContext.recordIds.length} élément(s)` : ''}{pageContext.ready === false ? ' · Chargement…' : ''}</p>}
        {wakeWord?.error && <p className="mt-2 text-xs text-red-700" role="alert">{wakeWord.error}</p>}
        {isInternal && pageContext?.kind !== 'documentation' && (
          <div className="mt-4 grid grid-cols-2 rounded-lg bg-black/15 p-1" role="radiogroup" aria-label="Type de documentation">
            {['functional', 'technical'].map(value => (
              <button key={value} type="button" role="radio" aria-checked={mode === value} onClick={() => setMode(value)} className={`rounded-md px-3 py-2 text-xs font-semibold ${mode === value ? 'bg-white text-[#523467]' : 'text-white/65 hover:text-white'}`}>
                {value === 'functional' ? 'Fonctionnel' : 'Technique'}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="dynamics-messages min-h-0 flex-1 space-y-5 overflow-y-auto bg-[#F7F7F9] px-4 py-5">
        {messages.map(message => (
          <div key={message.id} className={`flex items-start gap-2.5 ${message.role === 'user' ? 'justify-end' : ''}`}>
            {!floating && message.role !== 'user' && <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${message.role === 'error' ? 'bg-red-100 text-red-600' : 'bg-[#EDE5F0] text-[#63436F]'}`}>{message.role === 'error' ? <AlertCircle className="h-4 w-4" /> : <DynamicsIcon className="h-4 w-4" />}</span>}
            <div className={`min-w-0 max-w-[86%] ${message.role === 'user' ? 'rounded-2xl rounded-tr-sm bg-[#523467] px-4 py-3 text-white' : message.role === 'error' ? 'rounded-2xl rounded-tl-sm border border-red-200 bg-red-50 px-4 py-3 text-red-700' : 'dynamics-answer-bubble rounded-2xl rounded-bl-sm border border-[#e7dceb] bg-white px-4 py-3 shadow-sm'}`}>
              <div className="text-sm leading-6"><MessageText text={message.text} sources={message.sources} onOpen={openSource} /></div>

            </div>
            {!floating && message.role === 'user' && <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-200 text-slate-600"><User className="h-4 w-4" /></span>}
          </div>
        ))}
        {loading && <div className="flex items-center gap-3 text-sm text-slate-500"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#EDE5F0] text-[#63436F]"><DynamicsIcon className="h-4 w-4" /></span><span>Dynamix consulte les sources…</span></div>}
        <div ref={bottomRef} />
      </div>

      <footer className="border-t border-slate-200 bg-white p-4">
        {messages.length === 1 && (
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
            {suggestions.map(suggestion => <button key={suggestion} type="button" onClick={() => sendQuestion(suggestion)} className="shrink-0 rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:border-[#8A6993] hover:text-[#523467]">{suggestion}</button>)}
          </div>
        )}
        <form onSubmit={event => { event.preventDefault(); sendQuestion(); }} className="flex items-end gap-2">
          <DynamicsLiveControl awaitingCorrectionFeedback={Boolean(pendingUiAction && !pendingUiAction.feedback)} open={open} projectId={project?.odooProjectId} pageContext={pageContext} onQuestion={sendQuestion} onNavigateSource={openSource} onStatusChange={setLiveStatus} autoStartToken={autoStartLiveToken} autoStartPrompt={autoStartLivePrompt} busy={loading}>
          <textarea ref={inputRef} value={question} onChange={event => setQuestion(event.target.value)} onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
              event.preventDefault(); sendQuestion();
            }
          }} maxLength={500} rows={2} disabled={loading || !project?.odooProjectId || pageContext?.ready === false} placeholder="Posez une question…" aria-label="Votre question (Entrée pour envoyer, Maj + Entrée pour une nouvelle ligne)" className="min-w-0 min-h-[46px] flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#765381] focus:ring-2 focus:ring-[#765381]/10 disabled:bg-slate-100" />
          </DynamicsLiveControl>
        </form>
        <p className="mt-2 text-center text-[11px] text-slate-400">{!project?.odooProjectId ? 'Sélectionnez un projet pour poser une question.' : 'Réponses basées en priorité sur la page consultée et ses filtres.'}</p>
      </footer>
    </aside>
  );
}
